<template>
  <BaseModal @close="close">
    <div class="unsplash-layout">
      <ImageUploadUnsplashHeader @onClose="close" />

      <div class="unsplash-filters">
        <BaseTextInput
          name="unsplash-search"
          :placeholder="$t('image.unsplash.SEARCH_COLLECTION')"
          type="search"
          :label="$t('image.unsplash.SEARCH_COLLECTION')"
          :show-label="false"
          class="search-input"
          @change="search"
        />
      </div>

      <div ref="masonry" class="content-layout">
        <section key="loaded" class="images-and-loading-trigger-content">
          <ImageUploadUnsplashImages
            :images="images"
            :number-of-columns="NUM_OF_COLUMNS"
            :is-loading-images="isLoadingImages"
            :current-page="currentPage"
            :selected-image="selectedImage"
            @onSelectImage="selectImage"
            @onSubmitImage="submitImage"
          />

          <IntersectionObserver
            v-if="hasImages"
            key="loading-trigger"
            class="loading-trigger"
            @onIntersection="searchUnsplashPhotos"
          >
            <LoadingSpinner key="loading" :delay="0" />
          </IntersectionObserver>
        </section>

        <section v-if="!hasQuery" key="no-query" class="feedback-layout">
          <div class="feedback-layout__text">
            {{ $t('image.unsplash.SEARCH_PROMPT') }}
          </div>
        </section>

        <section v-if="noResults" key="no-results" class="feedback-layout">
          <img src="@/assets/img/search_image.svg" alt="" />
          <div class="feedback-layout__text">
            {{ $t('image.unsplash.NO_SEARCH_RESULTS', { query: query }) }}
          </div>
        </section>
      </div>

      <ImageUploadUnsplashFooter
        ref="unsplashFooter"
        :has-selected-image="hasSelectedImage"
        @onClose="close"
        @onSubmitImage="submitImage(selectedImage)"
      />
    </div>
  </BaseModal>
</template>

<script>
import { getSeason } from '@/helpers/misc'
import { getUnsplashPhotos } from '@/service/unsplash'

import BaseModal from '@/components/generics/base/BaseModal'
import BaseTextInput from '@/components/generics/base/BaseTextInput.vue'
import ImageUploadUnsplashFooter from '@/components/generics/imageUpload/ImageUploadUnsplashFooter'
import ImageUploadUnsplashHeader from '@/components/generics/imageUpload/ImageUploadUnsplashHeader'
import ImageUploadUnsplashImages from '@/components/generics/imageUpload/ImageUploadUnsplashImages'
import LoadingSpinner from '@/components/generics/LoadingSpinner'
import { IntersectionObserver } from '@/components/utility_components'

export default {
  components: {
    BaseModal,
    BaseTextInput,
    ImageUploadUnsplashFooter,
    ImageUploadUnsplashHeader,
    ImageUploadUnsplashImages,
    LoadingSpinner,
    IntersectionObserver,
  },

  emits: ['onClose', 'onSelectImage'],
  data() {
    return {
      MAX_PAGE: 10,
      NUM_OF_COLUMNS: 3,
      PAGE_SIZE: 24,

      columnHeights: [0, 0, 0],
      currentPage: 1,
      images: [],
      isLoadingImages: true,
      observer: null,
      query: getSeason(),
      selectedImage: {},
    }
  },
  computed: {
    hasImages() {
      return this.images.length > 0
    },

    hasSelectedImage() {
      return Object.keys(this.selectedImage).length !== 0
    },

    hasQuery() {
      return this.query !== ''
    },

    noResults() {
      return this.hasQuery && !this.hasImages && !this.isLoadingImages
    },
  },

  async created() {
    await this.searchUnsplashPhotos()
  },

  beforeUnmount() {
    this.reset()
  },

  methods: {
    close() {
      this.$emit('onClose')
    },

    reset() {
      this.columnHeights = [0, 0, 0]
      this.images = []
      this.currentPage = 1
    },

    async search(value) {
      this.query = value !== undefined ? value : ''

      if (this.query.length > 0) {
        this.reset()
        await this.searchUnsplashPhotos()
      }
    },

    async searchUnsplashPhotos() {
      if (this.currentPage < this.MAX_PAGE) {
        this.isLoadingImages = true

        const photos = await getUnsplashPhotos(
          this.query,
          this.currentPage,
          this.PAGE_SIZE
        )

        this.isLoadingImages = false

        if (photos?.length > 0) {
          this.masonry(photos)
          this.currentPage++
        }
      }
    },

    /**
     * Masonry
     * @param photos - a list of photos
     *
     * Distributes photos to each column based on the height.
     * - Each photo's height is calculated and kept in an accumulative log.
     * - Each new photo will then be added to the smallest column, to keep the columns relatively even.
     */
    masonry(photos) {
      if (!this.hasImages) {
        this.images = [[], [], []]
      }
      photos.forEach((photo) => {
        const minColumnValue = Math.min(...this.columnHeights)
        const targetColumn = this.columnHeights.findIndex(
          (column) => column === minColumnValue
        )
        this.images[targetColumn].push(photo)
        this.columnHeights[targetColumn] += this.calcPhotoDimensionsHeight(
          photo.width,
          photo.height
        )
      })
    },

    calcPhotoDimensionsHeight(width, height) {
      return height / width
    },

    selectImage(image) {
      this.selectedImage = image
      this.$refs.unsplashFooter.$refs.submitUnsplashImage.$el.focus()
    },

    submitImage(image) {
      this.$emit('onSelectImage', image)
      this.close()
    },
  },
}
</script>

<style lang="scss" scoped>
.unsplash-layout {
  padding: $spacing-m $spacing-l;
  min-width: 1200px;
  max-width: 1200px;

  @media screen and (max-width: $small-desktop-width-max) {
    min-width: 700px;
    max-width: 700px;
  }

  @media screen and (max-width: $tablet-width-max) {
    min-width: 100%;
    max-width: 100%;
  }
}

.unsplash-filters {
  margin: $spacing-3xs 0;
  width: 100%;
}

.content-layout {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 70vh;
  max-height: 70vh;
  width: 100%;

  @media screen and (max-width: $medium-desktop-width-max) {
    height: 65vh;
    max-height: 65vh;
  }
}

.images-and-loading-trigger-content {
  display: flex;
  flex-direction: column;
}

.loading-trigger {
  display: block;
  height: $spacing-5xs;
  width: 100%;
  margin-top: $spacing-3xs;
  padding: $spacing-xs;
}

.feedback-layout {
  @include subtitle;
  display: flex;
  flex-direction: column;
  gap: $spacing-s;
  height: 100%;
  justify-content: center;
  align-items: center;
  color: $color-text-light;
}
</style>
